import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Info } from 'lucide-react';
import RichText from '@/components/RichText';
import GuideImage from '@/components/GuideImage';
import { GUIDE_CHAPTERS } from '@/data/usageGuide';
import { headingId } from '@/lib/content';

/* =====================================================================
   BlogBlocks — yazı içeriğini (src/data/blog/posts/<slug>.js) çizer.
   Blok tipleri:
     { t: 'p', text }                         paragraf (RichText)
     { t: 'h2', text } / { t: 'h3', text }    başlık; h2'ler içindekilere girer
     { t: 'list', items, ordered? }           madde listesi
     { t: 'steps', items: [{ title, text }] } gerçek işlem adımları
     { t: 'table', head, rows, caption? }     tablo
     { t: 'guide', image, alt, caption? }     rehber ekran görüntüsü (büyütülebilir)
     { t: 'img', img: stock(...), caption? }  stok görsel (mobilde tam genişlik 16:10, sm+ sağa yaslı)
     { t: 'note', title?, text }              bilgi kutusu
     { t: 'cta', title, text, to, label }     yazıda en fazla bir tane
   ===================================================================== */

const GUIDE_HEIGHTS = Object.fromEntries(GUIDE_CHAPTERS.flatMap((c) => c.steps.map((s) => [s.image, s.height])));

const Block = ({ block }) => {
  switch (block.t) {
    case 'p':
      return (
        <p>
          <RichText text={block.text} />
        </p>
      );
    case 'h2':
      return (
        <h2 id={headingId(block.text)} className="type-h2 !mt-12 clear-both scroll-mt-24 text-balance">
          {block.text}
        </h2>
      );
    case 'h3':
      return <h3 className="type-h3 !mt-8 text-balance">{block.text}</h3>;
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag className={`space-y-2 pl-6 ${block.ordered ? 'list-decimal' : 'list-disc'} marker:text-brand-600`}>
          {block.items.map((item) => (
            <li key={item}>
              <RichText text={item} />
            </li>
          ))}
        </Tag>
      );
    }
    case 'steps':
      return (
        <ol className="space-y-4">
          {block.items.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span className="mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-ink-strong font-poppins text-sm font-semibold text-white">
                {i + 1}
              </span>
              <div>
                <p className="font-poppins font-semibold text-ink-strong">{step.title}</p>
                <p className="mt-1">
                  <RichText text={step.text} />
                </p>
              </div>
            </li>
          ))}
        </ol>
      );
    case 'table': {
      // 5+ sütun mobile sığmaz: yalnızca onlarda minimum genişlik + kaydırma ipucu.
      const wide = block.head.length >= 5;
      return (
        <figure>
          {wide && <p className="mb-1.5 text-xs text-ink-muted sm:hidden">Tabloyu yana kaydırabilirsiniz →</p>}
          <div className="overflow-x-auto rounded-2xl border border-hairline">
            <table
              className={`w-full border-collapse text-left text-[13.5px] sm:text-[15px] ${wide ? 'min-w-[560px]' : ''}`}
            >
              <thead className="bg-surface-2">
                <tr>
                  {block.head.map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="px-3 py-2.5 font-poppins font-semibold text-ink-strong sm:px-4 sm:py-3"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row) => (
                  <tr key={row.join('|')} className="border-t border-hairline">
                    {row.map((cell, i) => (
                      <td key={i} className="px-3 py-2.5 align-top sm:px-4 sm:py-3">
                        <RichText text={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.caption && <figcaption className="mt-2 text-sm text-ink-muted">{block.caption}</figcaption>}
        </figure>
      );
    }
    case 'guide':
      return (
        <figure className="!my-8">
          <GuideImage image={block.image} height={GUIDE_HEIGHTS[block.image]} alt={block.alt} />
          {block.caption && (
            <figcaption className="mt-2.5 text-center text-sm text-ink-muted">{block.caption}</figcaption>
          )}
        </figure>
      );
    case 'img':
      return (
        <figure className="w-full sm:float-right sm:mb-4 sm:ml-8 sm:mt-1 sm:w-[240px]">
          <img
            src={block.img.src}
            width={block.img.width}
            height={block.img.height}
            alt={block.img.alt}
            loading="lazy"
            decoding="async"
            className="aspect-[16/10] w-full rounded-2xl border border-hairline object-cover sm:aspect-auto sm:h-auto"
          />
          {block.caption && <figcaption className="mt-2 text-sm text-ink-muted">{block.caption}</figcaption>}
        </figure>
      );
    case 'note':
      return (
        <aside className="flex gap-3 rounded-2xl border border-brand-100 bg-brand-50/60 p-5">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-600" aria-hidden="true" />
          <div>
            {block.title && <p className="mb-1 font-poppins font-semibold text-ink-strong">{block.title}</p>}
            <p>
              <RichText text={block.text} />
            </p>
          </div>
        </aside>
      );
    case 'cta':
      return (
        <aside className="clear-both !my-10 rounded-2xl bg-ink-strong p-6 sm:p-8">
          <p className="mb-2 font-poppins text-xl font-semibold text-white">{block.title}</p>
          <p className="mb-5 !text-white/70">{block.text}</p>
          <Link to={block.to} className="modern-btn-on-dark">
            {block.label}
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </aside>
      );
    default:
      return null;
  }
};

const BlogBlocks = ({ blocks }) => (
  <div className="space-y-5 font-inter text-[17px] leading-[1.75] text-ink">
    {blocks.map((block, i) => (
      <Block key={i} block={block} />
    ))}
    <div className="clear-both" />
  </div>
);

export default BlogBlocks;
